import { parseCloseHour, parseHour, slotTime } from './schedule.utils';

export interface GridSlot {
  hour: number;
  half: 0 | 1;
}

/** Visible 30-min slots between opening and closing times (exclusive end). */
export function buildVisibleSlots(
  openTime: string | undefined,
  closeTime: string | undefined,
): GridSlot[] {
  const open  = parseHour(openTime ?? '08:00');
  const close = parseCloseHour(closeTime ?? '22:00', openTime);
  const out: GridSlot[] = [];
  for (let h = Math.floor(open); h < 24; h++) {
    for (const half of [0, 1] as const) {
      const frac = h + half * 0.5;
      if (frac < open || frac >= close) continue;
      out.push({ hour: h, half });
    }
  }
  return out;
}

/** Exact slot index for a fractional hour, or -1. */
export function findSlotIndex(slots: GridSlot[], frac: number): number {
  return slots.findIndex(s => Math.abs(s.hour + s.half * 0.5 - frac) < 0.001);
}

/** Nearest slot index for a fractional hour (clamped to visible range). */
export function slotIndexForFraction(slots: GridSlot[], frac: number): number {
  const exact = findSlotIndex(slots, frac);
  if (exact !== -1) return exact;
  const next = slots.findIndex(s => s.hour + s.half * 0.5 > frac);
  return next === -1 ? slots.length : Math.max(0, next);
}

export interface BusyRun {
  startIdx: number;
  spanSlots: number;
}

/** Merge consecutive occupied slots into visual blocks. */
export function mergeBusyRuns<T>(busyMeta: (T | null)[]): BusyRun[] {
  const runs: BusyRun[] = [];
  let i = 0;
  while (i < busyMeta.length) {
    if (busyMeta[i]) {
      let j = i + 1;
      while (j < busyMeta.length && busyMeta[j]) j++;
      runs.push({ startIdx: i, spanSlots: j - i });
      i = j;
    } else {
      i++;
    }
  }
  return runs;
}

/** Slot fractions for quick range checks. */
export function slotFractions(slots: GridSlot[]): number[] {
  return slots.map(s => s.hour + s.half * 0.5);
}

export { slotTime };
